import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { usePrivyAccount } from '@/hooks';
import useFundedAccount from '@/hooks/use-funded-account';
import {
  getMigrationStatusQueryKey,
  useMigrationStatus,
} from '@/lib/api/migration';
import {
  Account,
  useAccountStore,
  useCurrentAccount,
} from '@/store/use-account-store';
import {
  selectIsMigrationCompleted,
  useMigrationStore,
} from '@/store/use-migration-store';
import { CapitalStatus } from '@/types';

const EMPTY_ACCOUNTS: string[] = [];

const isCapitalAccount = (status: CapitalStatus) =>
  status !== CapitalStatus.NOT_ELIGIBLE && status !== CapitalStatus.UNKNOWN;

export default function MigrationHandler() {
  const { address, hasAuth } = useAccountStore(
    useShallow((state) => ({
      address: state.address,
      hasAuth: state.hasAuth,
    })),
  );
  const { data } = useMigrationStatus();
  const isAccountEmpty = useIsAccountEmpty();
  const orderGateLockedAt = useMigrationStore(
    (state) => state.orderGateLockedAt,
  );
  const isMigrated = useMigrationStore(selectIsMigrationCompleted);
  const migratedAccounts = useMigrationStore(
    useShallow((state) =>
      state.status?.started ? state.status.accounts : EMPTY_ACCOUNTS,
    ),
  );
  const queryClient = useQueryClient();

  useEffect(() => {
    useMigrationStore.getState().reset();
  }, [address, hasAuth]);

  useEffect(() => {
    if (!hasAuth || !address) return;
    useMigrationStore.getState().setStatus(data ?? null);
  }, [data, hasAuth, address]);

  useEffect(() => {
    if (!hasAuth || !address) return;
    useMigrationStore.getState().setIsAccountEmpty(isAccountEmpty);
  }, [isAccountEmpty, hasAuth, address]);

  useEffect(() => {
    if (orderGateLockedAt === null) return;
    queryClient.invalidateQueries({
      queryKey: getMigrationStatusQueryKey(address),
    });
  }, [orderGateLockedAt, address, queryClient]);

  useEffect(() => {
    if (!isMigrated || migratedAccounts.length === 0) return;
    const migrated = new Set(migratedAccounts);
    const { accounts, setAccount, getAccurateTime, updateAccount } =
      useAccountStore.getState();
    Object.entries(accounts).forEach(([accountAddress, account]) => {
      if (!migrated.has(accountAddress.toLowerCase())) return;
      if (Object.keys(account.positions).length === 0) return;
      setAccount({ positions: {} }, getAccurateTime(), accountAddress);
      updateAccount({ address: accountAddress });
    });
  }, [isMigrated, migratedAccounts]);

  return null;
}

function useIsAccountEmpty(): boolean {
  const { address: primaryAddress } = usePrivyAccount();
  const { capitalStatusData, isStatusLoading, isFundedOrEval } =
    useFundedAccount();
  const main = useCurrentAccount(selectEmptiness, primaryAddress);
  const session = useCurrentAccount(selectEmptiness);

  if (isFundedOrEval) return false;
  if (isStatusLoading || !capitalStatusData) return false;
  if (
    Object.values(capitalStatusData).some((entry) =>
      isCapitalAccount(entry.capital_status),
    )
  ) {
    return false;
  }
  return isEmpty(main) && isEmpty(session);
}

const selectEmptiness = (account: Account) => ({
  hasHydrated: account.hasHydrated,
  collateral: account.collateral,
  positionCount: Object.keys(account.positions).length,
});

const isEmpty = (account: ReturnType<typeof selectEmptiness>) =>
  account.hasHydrated &&
  account.collateral === 0n &&
  account.positionCount === 0;
